import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye,
  Clock,
  Phone,
  MessageSquare,
  Share2,
  Globe,
  Star,
  Heart,
  Award,
  ShieldCheck,
  Crown,
  Building2,
  Calendar,
  ArrowUpRight,
  ChevronRight,
  ExternalLink,
  Sparkles,
  BarChart3,
  RefreshCw,
  Flame,
  Layers,
  Filter
} from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const base = isLocal ? 'http://localhost:8080' : '';
  if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
  return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
};

const calculatePresetDates = (preset) => {
  const end = new Date();
  const start = new Date();
  if (preset === '7d') {
    start.setDate(end.getDate() - 7);
  } else if (preset === '1m') {
    start.setDate(end.getDate() - 30);
  } else if (preset === '6m') {
    start.setMonth(end.getMonth() - 6);
  } else if (preset === '1y') {
    start.setFullYear(end.getFullYear() - 1);
  } else if (preset === 'all') {
    start.setFullYear(end.getFullYear() - 5);
  }
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return {
    startDate: fmt(start),
    endDate: fmt(end)
  };
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const VendorDashboard = () => {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  // Timeframe filter: '7d' | '1m' | '6m' | '1y' | 'all' | 'custom'
  const [dateRangePreset, setDateRangePreset] = useState('1m');
  const [customDates, setCustomDates] = useState(() => calculatePresetDates('1m'));
  const [activeChartMetric, setActiveChartMetric] = useState('views'); // 'views' | 'impressions' | 'calls' | 'whatsapp' | 'inquiries'

  const fetchDashboard = useCallback(async (sDate = customDates.startDate, eDate = customDates.endDate, isSilent = false) => {
    if (!sDate || !eDate) return;
    if (!isSilent) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await vendorService.getVendorDashboardAnalytics(sDate, eDate);
      if (res.success && res.data) {
        setDashboardData(res.data);
      } else {
        toast.error(res.message || 'Failed to fetch vendor analytics');
      }
    } catch (err) {
      console.error('Error loading vendor dashboard analytics:', err);
      toast.error('Unable to connect to analytics service');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customDates.startDate, customDates.endDate, toast]);

  useEffect(() => {
    if (customDates.startDate && customDates.endDate) {
      fetchDashboard(customDates.startDate, customDates.endDate);
    }
  }, [customDates.startDate, customDates.endDate, fetchDashboard]);

  const handlePresetSelect = (presetId) => {
    setDateRangePreset(presetId);
    const dates = calculatePresetDates(presetId);
    setCustomDates(dates);
  };

  const handleCustomDateChange = (start, end) => {
    if (start && end) {
      setCustomDates({ startDate: start, endDate: end });
      setDateRangePreset('custom');
    } else if (!start && !end) {
      handlePresetSelect('1m');
    }
  };

  // Loading Skeleton State
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="w-16 h-16 bg-gray-200 rounded-2xl"></div>
            <div className="space-y-2 flex-1">
              <div className="h-5 bg-gray-200 rounded w-48"></div>
              <div className="h-3.5 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
          <div className="h-10 bg-gray-200 rounded-xl w-64"></div>
        </div>

        {/* KPI Grid Skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <div key={n} className="bg-white rounded-2xl p-5 border border-gray-100 h-32 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-24"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-28"></div>
            </div>
          ))}
        </div>

        {/* Chart Skeleton */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 h-80"></div>
      </div>
    );
  }

  // No Listing State
  if (!dashboardData || !dashboardData.hasListing) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 sm:p-12 text-center max-w-2xl mx-auto shadow-sm my-6">
        <div className="w-20 h-20 bg-teal-50 text-[#2ab391] rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-inner">
          <Building2 size={36} />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          No Active Vendor Listing Found
        </h2>
        <p className="text-gray-500 text-sm mb-6 leading-relaxed">
          Create and publish your business profile on Top Interiors India to start tracking real-time impressions, page visits, customer phone calls, WhatsApp leads, and reviews.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/my-account/manage-listing"
            className="w-full sm:w-auto px-6 py-3 bg-[#2ab391] hover:bg-[#23977a] text-white font-semibold rounded-xl text-sm transition-all shadow-md flex items-center justify-center gap-2"
          >
            <span>Create Listing Profile</span>
            <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const {
    listingSummary,
    kpis,
    funnel,
    trends = [],
    inquiryStatusBreakdown,
    actionChannelsBreakdown,
    recentInquiries = [],
    recentReviews = [],
    growthRecommendations = []
  } = dashboardData;

  // Compute trend metrics for SVG Chart
  const trendPoints = trends || [];
  const maxMetricVal = Math.max(
    1,
    ...trendPoints.map((p) => {
      if (activeChartMetric === 'impressions') return p.impressions || 0;
      if (activeChartMetric === 'calls') return p.calls || 0;
      if (activeChartMetric === 'whatsapp') return p.whatsapp || 0;
      if (activeChartMetric === 'inquiries') return p.inquiries || 0;
      return p.views || 0;
    })
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* ── 1. Top Header Profile & Health Banner ───────────────────── */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-850 to-slate-900 text-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 shadow-xl border border-gray-800 relative overflow-hidden">
        {/* Background decorative glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2ab391]/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-teal-500/5 rounded-full blur-2xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Business Info */}
          <div className="flex items-start sm:items-center gap-4 min-w-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-1 flex-shrink-0 flex items-center justify-center overflow-hidden shadow-md">
              {listingSummary.logoUrl ? (
                <img
                  src={getImageUrl(listingSummary.logoUrl)}
                  alt={listingSummary.companyName}
                  className="w-full h-full object-contain rounded-xl"
                />
              ) : (
                <Building2 size={32} className="text-teal-300" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-white tracking-tight truncate">
                  {listingSummary.companyName}
                </h1>
                {listingSummary.membershipPlan === 'PREMIUM' && (
                  <span className="bg-amber-400 text-gray-950 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm uppercase tracking-wider shrink-0">
                    <Crown size={11} className="fill-current" />
                    <span>PREMIUM</span>
                  </span>
                )}
                {listingSummary.membershipPlan === 'PROFESSIONAL' && (
                  <span className="bg-[#2ab391] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm uppercase tracking-wider shrink-0">
                    <ShieldCheck size={11} />
                    <span>PRO</span>
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-300">
                {listingSummary.categoryName && (
                  <span className="font-medium text-teal-300">{listingSummary.categoryName}</span>
                )}
                <span>
                  {[listingSummary.area, listingSummary.city, listingSummary.state].filter(Boolean).join(', ')}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-emerald-300 font-semibold">{listingSummary.status}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {/* View live public listing */}
            <button
              onClick={() => window.open(`/listing/${slugify(listingSummary.companyName)}`, '_blank')}
              className="flex-1 sm:flex-initial px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold backdrop-blur-sm border border-white/15 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
              title="Open Public Listing in New Tab"
            >
              <span>Live Profile</span>
              <ExternalLink size={13} />
            </button>

            {/* Refresh */}
            <button
              onClick={() => fetchDashboard(customDates.startDate, customDates.endDate, true)}
              disabled={refreshing}
              className="shrink-0 px-3 sm:px-3.5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold backdrop-blur-sm border border-white/15 transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              title="Refresh Analytics"
            >
              <RefreshCw size={13} className={refreshing ? 'animate-spin text-teal-300' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Secondary Bar inside Banner: Health Score & Profile Completeness */}
        <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Priority Ranking Score */}
          <div className="bg-white/5 backdrop-blur-xs rounded-xl p-3.5 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                Algorithm Ranking Score
              </div>
              <div className="text-xl font-bold text-white flex items-baseline gap-1 mt-0.5">
                <span>{listingSummary.priorityScore || 0}</span>
                <span className="text-xs text-gray-400 font-normal">/ 100 pts</span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center">
              <Award size={20} />
            </div>
          </div>

          {/* Profile Completeness Meter */}
          <div className="bg-white/5 backdrop-blur-xs rounded-xl p-3.5 border border-white/10 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                Profile Completeness
              </span>
              <span className="text-xs font-bold text-emerald-400">
                {listingSummary.completenessPercentage || 0}%
              </span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2 mt-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-teal-400 to-emerald-400 h-full rounded-full transition-all duration-500"
                style={{ width: `${listingSummary.completenessPercentage || 0}%` }}
              ></div>
            </div>
          </div>

          {/* Average Rating */}
          <div className="bg-white/5 backdrop-blur-xs rounded-xl p-3.5 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                Average Rating
              </div>
              <div className="text-xl font-bold text-white flex items-center gap-1.5 mt-0.5">
                <span>{kpis.averageRating || 0}</span>
                <div className="flex text-amber-400 text-xs">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={12}
                      className={s <= Math.round(kpis.averageRating || 0) ? 'fill-current' : 'text-gray-600'}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-400 font-medium text-right">
              {kpis.totalReviews} review{kpis.totalReviews === 1 ? '' : 's'}
            </div>
          </div>

          {/* Membership Tier / Active Plan */}
          <div className="bg-white/5 backdrop-blur-xs rounded-xl p-3.5 border border-white/10 flex flex-col justify-between">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                Active Plan
              </span>
              {listingSummary.membershipPlan === 'FREE' ? (
                <Link
                  to="/my-account/upgrade-membership"
                  className="text-[11px] bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 text-white font-bold px-2.5 py-0.5 rounded-md shadow-xs transition-all flex items-center gap-1"
                >
                  Upgrade
                </Link>
              ) : (
                <span className="text-[11px] font-bold text-emerald-300 bg-emerald-500/20 border border-emerald-500/30 px-2 py-0.5 rounded-md flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span>Active</span>
                </span>
              )}
            </div>
            <div className="text-lg sm:text-xl font-bold text-teal-300 tracking-tight truncate mt-1">
              {listingSummary.membershipPlan}
            </div>
          </div>
        </div>
      </div>

      {/* ── 2. Timeframe & Date Range Filter Toolbar ─────────────────── */}
      <div className="bg-white p-3.5 sm:p-4 rounded-2xl border border-gray-100 shadow-xs flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
        {/* Left: Presets */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-700 shrink-0">
            <div className="w-7 h-7 rounded-lg bg-teal-50 text-[#2ab391] flex items-center justify-center">
              <Calendar size={14} />
            </div>
            <span>Analytics Timeframe:</span>
          </div>

          {/* Preset Buttons */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0" style={{ scrollbarWidth: 'none' }}>
            {[
              { id: '7d', label: '7 Days' },
              { id: '1m', label: '1 Month' },
              { id: '6m', label: '6 Months' },
              { id: '1y', label: '1 Year' },
              { id: 'all', label: 'All Time' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handlePresetSelect(tab.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  dateRangePreset === tab.id
                    ? 'bg-[#2ab391] text-white shadow-sm ring-2 ring-[#2ab391]/20 font-bold'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200/80 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Custom Date Range Picker */}
        <div className="flex items-center gap-2 pt-2.5 lg:pt-0 border-t lg:border-t-0 border-gray-100 shrink-0">
          <div className="w-full sm:w-56">
            <DateRangePicker
              startDate={customDates.startDate}
              endDate={customDates.endDate}
              onChange={handleCustomDateChange}
              placeholder="Custom Date Range"
              className="w-full h-8.5 text-xs font-medium"
            />
          </div>
        </div>
      </div>

      {/* ── 3. Core 8 KPI Metric Cards Grid ─────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Impressions */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Total Impressions
            </span>
            <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center transition-transform group-hover:scale-110">
              <Layers size={16} />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {kpis.totalImpressions.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500 font-medium">
            Search &amp; category appearances
          </div>
        </div>

        {/* Profile Views */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Page Views
            </span>
            <div className="w-8 h-8 rounded-xl bg-teal-50 text-[#2ab391] flex items-center justify-center transition-transform group-hover:scale-110">
              <Eye size={16} />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight flex items-baseline gap-2">
            <span>{kpis.totalViews.toLocaleString()}</span>
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
              {kpis.uniqueViews} unique
            </span>
          </div>
          <div className="mt-1 text-[11px] text-gray-500 font-medium">
            Direct clicks &amp; full profile visits
          </div>
        </div>

        {/* Average Stay Time (Dwell Duration) */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Avg. Stay Time
            </span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center transition-transform group-hover:scale-110">
              <Clock size={16} />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {kpis.avgStayFormatted || '0s'}
          </div>
          <div className="mt-1 text-[11px] text-gray-500 font-medium">
            Calculated visitor engagement duration
          </div>
        </div>

        {/* Inquiries */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Direct Inquiries
            </span>
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center transition-transform group-hover:scale-110">
              <MessageSquare size={16} />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {kpis.totalInquiries.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-blue-600 font-semibold flex items-center gap-1">
            <span>{inquiryStatusBreakdown.NEW} New leads</span>
            <ChevronRight size={12} />
          </div>
        </div>

        {/* Direct Phone Calls */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Phone Calls
            </span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center transition-transform group-hover:scale-110">
              <Phone size={16} />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {kpis.totalCalls.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500 font-medium">
            Initiated via Call button
          </div>
        </div>

        {/* WhatsApp Conversations */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              WhatsApp Chats
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#25D366]/10 text-[#25D366] flex items-center justify-center transition-transform group-hover:scale-110">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {kpis.totalWhatsApp.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500 font-medium">
            WhatsApp inquiry clicks
          </div>
        </div>

        {/* Website & Shares */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Website &amp; Shares
            </span>
            <div className="w-8 h-8 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center transition-transform group-hover:scale-110">
              <Share2 size={16} />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight flex items-baseline gap-2">
            <span>{(kpis.totalWebsiteClicks + kpis.totalShares).toLocaleString()}</span>
            <span className="text-xs font-medium text-gray-500">
              ({kpis.totalWebsiteClicks} web, {kpis.totalShares} share)
            </span>
          </div>
          <div className="mt-1 text-[11px] text-gray-500 font-medium">
            Outbound traffic &amp; social shares
          </div>
        </div>

        {/* Saved to Favorites / Bookmarks */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              User Bookmarks
            </span>
            <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center transition-transform group-hover:scale-110">
              <Heart size={16} className="fill-rose-500" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {kpis.totalFavorites.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500 font-medium">
            Customers bookmarked your listing
          </div>
        </div>
      </div>

      {/* ── 4. Conversion Funnel Progress Pipeline ───────────────────── */}
      <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-gray-100 shadow-xs">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              Visitor Conversion Funnel
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Visual path from search impressions to inquiries and calls
            </p>
          </div>
          <span className="text-xs font-bold text-teal-600 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
            {funnel.conversionRatePercent}% Overall Conversion
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative">
          {/* Step 1: Impressions */}
          <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 flex flex-col justify-between relative">
            <div>
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                1. Impressions
              </span>
              <div className="text-xl sm:text-2xl font-black text-gray-900 mt-1">
                {funnel.impressions.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Showed up in directory results
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-200/60 flex items-center justify-between text-xs font-medium text-gray-600">
              <span>View CTR:</span>
              <span className="font-bold text-teal-600">{funnel.viewCtrPercent}%</span>
            </div>
          </div>

          {/* Step 2: Page Views */}
          <div className="bg-teal-50/50 rounded-2xl p-4 border border-teal-100 flex flex-col justify-between relative">
            <div>
              <span className="text-[11px] font-bold text-teal-700 uppercase tracking-wider">
                2. Profile Views
              </span>
              <div className="text-xl sm:text-2xl font-black text-gray-900 mt-1">
                {funnel.views.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Visitors clicked &amp; viewed profile
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-teal-100 flex items-center justify-between text-xs font-medium text-teal-800">
              <span>Action Rate:</span>
              <span className="font-bold text-teal-600">{funnel.actionRatePercent}%</span>
            </div>
          </div>

          {/* Step 3: Action Clicks */}
          <div className="bg-emerald-50/50 rounded-2xl p-4 border border-emerald-100 flex flex-col justify-between relative">
            <div>
              <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                3. Direct Actions
              </span>
              <div className="text-xl sm:text-2xl font-black text-gray-900 mt-1">
                {funnel.actionClicks.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Calls, WhatsApp, Web, Shares
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-emerald-100 flex items-center justify-between text-xs font-medium text-emerald-800">
              <span>Lead Quality:</span>
              <span className="font-bold text-emerald-600">High Intent</span>
            </div>
          </div>

          {/* Step 4: Inquiries */}
          <div className="bg-blue-50/50 rounded-2xl p-4 border border-blue-100 flex flex-col justify-between relative">
            <div>
              <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                4. Formal Inquiries
              </span>
              <div className="text-xl sm:text-2xl font-black text-gray-900 mt-1">
                {funnel.inquiries.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Verified booking / quotation forms
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-blue-100 flex items-center justify-between text-xs font-medium text-blue-800">
              <span>Lead Pipeline:</span>
              <span className="font-bold text-blue-600">{inquiryStatusBreakdown.WON} Won Deals</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 5. Time-Series Trends Interactive Chart ───────────────────── */}
      <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-gray-100 shadow-xs relative">
        {/* Subtle updating overlay */}
        {refreshing && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-2xl sm:rounded-3xl z-20 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 bg-gray-900 text-white text-xs font-semibold px-3.5 py-1.5 rounded-full shadow-lg">
              <RefreshCw size={12} className="animate-spin text-teal-300" />
              <span>Updating data...</span>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base sm:text-lg font-bold text-gray-900">
                Performance Timeline &amp; Traffic Trends
              </h3>
              <span className="text-[10px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md border border-teal-100 uppercase tracking-wider">
                {trendPoints.length > 45 || dateRangePreset === '6m' || dateRangePreset === '1y' || dateRangePreset === 'all'
                  ? 'Monthly Breakdown'
                  : 'Daily Breakdown'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {formatDisplayDate(customDates.startDate)} – {formatDisplayDate(customDates.endDate)} ({trendPoints.length} data interval{trendPoints.length === 1 ? '' : 's'})
            </p>
          </div>

          {/* Metric Selector Tabs */}
          <div className="flex flex-wrap items-center gap-1.5 shrink-0">
            {[
              { id: 'views', label: 'Views', color: '#2ab391' },
              { id: 'impressions', label: 'Impressions', color: '#9333ea' },
              { id: 'calls', label: 'Calls', color: '#059669' },
              { id: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
              { id: 'inquiries', label: 'Inquiries', color: '#2563eb' }
            ].map((metric) => (
              <button
                key={metric.id}
                onClick={() => setActiveChartMetric(metric.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                  activeChartMetric === metric.id
                    ? 'bg-gray-900 text-white shadow-sm ring-2 ring-gray-900/20'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200/80 hover:text-gray-900'
                }`}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: metric.color }}></span>
                <span>{metric.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* SVG Interactive Timeline Chart */}
        {trendPoints.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
            <BarChart3 size={36} className="text-gray-300 stroke-[1.5]" />
            <span>No activity recorded for this date range yet.</span>
          </div>
        ) : (
          <div className="w-full">
            <div className="h-64 sm:h-72 flex items-end gap-1.5 sm:gap-2.5 pt-14 pb-2 border-b border-gray-100 relative">
              {/* Background Grid Lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-25">
                <div className="border-b border-gray-200 w-full"></div>
                <div className="border-b border-gray-200 w-full"></div>
                <div className="border-b border-gray-200 w-full"></div>
                <div className="border-b border-gray-200 w-full"></div>
              </div>

              {/* Bars */}
              {trendPoints.map((item, idx) => {
                let currentVal = 0;
                let barColor = '#2ab391';
                if (activeChartMetric === 'impressions') {
                  currentVal = item.impressions || 0;
                  barColor = '#9333ea';
                } else if (activeChartMetric === 'calls') {
                  currentVal = item.calls || 0;
                  barColor = '#059669';
                } else if (activeChartMetric === 'whatsapp') {
                  currentVal = item.whatsapp || 0;
                  barColor = '#25D366';
                } else if (activeChartMetric === 'inquiries') {
                  currentVal = item.inquiries || 0;
                  barColor = '#2563eb';
                } else {
                  currentVal = item.views || 0;
                  barColor = '#2ab391';
                }

                // Capped at 78% so tooltip has plenty of headroom at the top
                const heightPercent = maxMetricVal > 0 ? (currentVal / maxMetricVal) * 78 : 0;
                const maxBarWidth = trendPoints.length <= 8
                  ? 'max-w-[48px]'
                  : trendPoints.length <= 15
                  ? 'max-w-[32px]'
                  : trendPoints.length <= 31
                  ? 'max-w-[18px]'
                  : 'max-w-[10px] sm:max-w-[14px]';

                const isFirst = idx === 0;
                const isLast = idx === trendPoints.length - 1;

                return (
                  <div
                    key={item.date || idx}
                    className="flex-1 h-full flex flex-col items-center justify-end relative group cursor-pointer min-w-0"
                  >
                    {/* Tooltip on Hover */}
                    <div
                      className={`absolute bottom-full mb-2 hidden group-hover:flex flex-col z-30 pointer-events-none transition-opacity ${
                        isFirst
                          ? 'left-0 items-start'
                          : isLast
                          ? 'right-0 items-end'
                          : 'left-1/2 -translate-x-1/2 items-center'
                      }`}
                    >
                      <div className="bg-gray-900/95 backdrop-blur-md text-white text-[11px] font-semibold py-1.5 px-2.5 rounded-xl shadow-xl whitespace-nowrap border border-gray-700/60">
                        <div className="text-gray-300 text-[10px] font-medium">{item.label}</div>
                        <div className="text-white font-bold flex items-center gap-1.5 mt-0.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: barColor }}></span>
                          <span className="capitalize">{currentVal.toLocaleString()} {activeChartMetric}</span>
                        </div>
                      </div>
                      <div
                        className={`w-2 h-2 bg-gray-900/95 rotate-45 -mt-1 border-r border-b border-gray-700/60 ${
                          isFirst ? 'ml-3' : isLast ? 'mr-3' : ''
                        }`}
                      ></div>
                    </div>

                    {/* Bar */}
                    <div
                      className={`w-full ${maxBarWidth} rounded-t-lg transition-all duration-300 group-hover:brightness-110 shadow-xs`}
                      style={{
                        height: currentVal > 0 ? `${Math.max(heightPercent, 6)}%` : '6px',
                        backgroundColor: currentVal > 0 ? barColor : '#e2e8f0'
                      }}
                    ></div>
                  </div>
                );
              })}
            </div>

            {/* Date Labels Directly Aligned Below Each Bar or at clean intervals */}
            <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-400 mt-2.5 font-medium px-1">
              {trendPoints.length <= 12 ? (
                trendPoints.map((item, idx) => (
                  <span key={item.date || idx} className="text-center truncate px-0.5 flex-1">
                    {item.label}
                  </span>
                ))
              ) : (
                <>
                  <span>{trendPoints[0]?.label}</span>
                  <span>{trendPoints[Math.floor(trendPoints.length * 0.25)]?.label}</span>
                  <span>{trendPoints[Math.floor(trendPoints.length * 0.5)]?.label}</span>
                  <span>{trendPoints[Math.floor(trendPoints.length * 0.75)]?.label}</span>
                  <span>{trendPoints[trendPoints.length - 1]?.label}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── 6. Lead Pipeline & Action Channels Distribution ──────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inquiry Status Distribution */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-gray-100 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Flame size={18} className="text-amber-500" />
                <span>Inquiry Pipeline Breakdown</span>
              </h3>
              <Link
                to="/my-account/inquiries"
                className="text-xs font-semibold text-[#2ab391] hover:underline flex items-center gap-1"
              >
                <span>Manage Inquiries</span>
                <ChevronRight size={13} />
              </Link>
            </div>

            <div className="space-y-3 mt-4">
              {[
                { label: 'Hot Leads', count: inquiryStatusBreakdown.HOT, color: 'bg-rose-500', text: 'text-rose-600', bg: 'bg-rose-50' },
                { label: 'Warm Leads', count: inquiryStatusBreakdown.WARM, color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50' },
                { label: 'New / Pending', count: inquiryStatusBreakdown.NEW, color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Won Projects', count: inquiryStatusBreakdown.WON, color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
                { label: 'Cold / Inactive', count: inquiryStatusBreakdown.COLD, color: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50' },
                { label: 'Lost', count: inquiryStatusBreakdown.LOST, color: 'bg-red-400', text: 'text-red-600', bg: 'bg-red-50' }
              ].map((item) => {
                const totalInq = Math.max(kpis.totalInquiries, 1);
                const percent = Math.round((item.count / totalInq) * 100);
                return (
                  <div key={item.label} className="space-y-1">
                    <div className="flex items-center justify-between text-xs font-medium">
                      <span className="text-gray-700">{item.label}</span>
                      <span className={`font-bold ${item.text}`}>{item.count} ({percent}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`${item.color} h-full rounded-full transition-all duration-300`}
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action Channels Distribution */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-gray-100 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 size={18} className="text-[#2ab391]" />
                <span>Customer Action Channels</span>
              </h3>
              <span className="text-xs text-gray-400 font-medium">Contact Preference</span>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-emerald-50/60 rounded-2xl p-4 border border-emerald-100">
                <div className="flex items-center gap-2 text-emerald-700 mb-1">
                  <Phone size={16} />
                  <span className="text-xs font-bold uppercase">Phone Calls</span>
                </div>
                <div className="text-2xl font-black text-gray-900 mt-1">
                  {actionChannelsBreakdown.CALL}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">Direct phone connects</div>
              </div>

              <div className="bg-[#25D366]/10 rounded-2xl p-4 border border-[#25D366]/20">
                <div className="flex items-center gap-2 text-[#25D366] mb-1">
                  <MessageSquare size={16} />
                  <span className="text-xs font-bold uppercase">WhatsApp</span>
                </div>
                <div className="text-2xl font-black text-gray-900 mt-1">
                  {actionChannelsBreakdown.WHATSAPP}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">WhatsApp inquiries</div>
              </div>

              <div className="bg-teal-50/60 rounded-2xl p-4 border border-teal-100">
                <div className="flex items-center gap-2 text-teal-700 mb-1">
                  <Globe size={16} />
                  <span className="text-xs font-bold uppercase">Website Visits</span>
                </div>
                <div className="text-2xl font-black text-gray-900 mt-1">
                  {actionChannelsBreakdown.WEBSITE}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">Outbound site clicks</div>
              </div>

              <div className="bg-sky-50/60 rounded-2xl p-4 border border-sky-100">
                <div className="flex items-center gap-2 text-sky-700 mb-1">
                  <Share2 size={16} />
                  <span className="text-xs font-bold uppercase">Social Shares</span>
                </div>
                <div className="text-2xl font-black text-gray-900 mt-1">
                  {actionChannelsBreakdown.SHARE}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">Shares on IG, FB, WA, X</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 7. Recent Inquiries & Recent Reviews Feeds ───────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Inquiries List */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-gray-100 shadow-xs">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-900">Recent Customer Inquiries</h3>
            <Link
              to="/my-account/inquiries"
              className="text-xs font-semibold text-[#2ab391] hover:underline flex items-center gap-0.5"
            >
              <span>View All</span>
              <ChevronRight size={14} />
            </Link>
          </div>

          {recentInquiries.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs">
              No inquiries received yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentInquiries.map((inq) => (
                <div key={inq.id} className="py-3 first:pt-0 last:pb-0 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 text-sm truncate">{inq.name}</span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                          inq.status === 'HOT'
                            ? 'bg-rose-100 text-rose-700'
                            : inq.status === 'WARM'
                            ? 'bg-amber-100 text-amber-700'
                            : inq.status === 'WON'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {inq.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                      {inq.message || 'No description provided'}
                    </p>
                    <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-2">
                      {inq.phone && <span>📞 {inq.phone}</span>}
                      {inq.created_at && <span>• {inq.created_at}</span>}
                    </div>
                  </div>
                  <Link
                    to="/my-account/inquiries"
                    className="p-1.5 rounded-lg bg-gray-50 hover:bg-teal-50 text-gray-400 hover:text-[#2ab391] transition-colors shrink-0"
                    title="Open in Inquiries Manager"
                  >
                    <ArrowUpRight size={15} />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Customer Reviews */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-gray-100 shadow-xs">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-900">Recent Customer Reviews</h3>
            <Link
              to="/my-account/reviews"
              className="text-xs font-semibold text-[#2ab391] hover:underline flex items-center gap-0.5"
            >
              <span>View All</span>
              <ChevronRight size={14} />
            </Link>
          </div>

          {recentReviews.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs">
              No customer reviews submitted yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentReviews.map((rev) => (
                <div key={rev.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <div className="flex text-amber-400">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            size={12}
                            className={s <= rev.rating ? 'fill-current' : 'text-gray-200'}
                          />
                        ))}
                      </div>
                      <span className="text-xs font-bold text-gray-800">{rev.rating}.0</span>
                    </div>
                    <span className="text-[11px] text-gray-400">{rev.created_at}</span>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2 italic">
                    "{rev.comment || 'Great service!'}"
                  </p>
                  {rev.reply && (
                    <div className="mt-1.5 pl-3 border-l-2 border-[#2ab391] text-[11px] text-teal-800 bg-teal-50/50 p-1.5 rounded-r-md">
                      <span className="font-semibold">Your Reply:</span> {rev.reply}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 8. Growth & Ranking Recommendations ──────────────────────── */}
      {growthRecommendations.length > 0 && (
        <div className="bg-gradient-to-r from-teal-500/10 via-emerald-500/10 to-teal-500/5 rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-teal-200/60 shadow-xs">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-xl bg-[#2ab391] text-white flex items-center justify-center shadow-sm">
              <Sparkles size={16} />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900">
                Pro Growth Recommendations
              </h3>
              <p className="text-xs text-gray-500">
                Actionable tips to boost listing priority and attract more clients
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {growthRecommendations.map((tip, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-4 border border-teal-100 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-900">{tip.title}</span>
                    {tip.impact && (
                      <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200 shrink-0">
                        {tip.impact}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed mb-4">
                    {tip.description}
                  </p>
                </div>
                <Link
                  to={tip.actionUrl}
                  className="w-full py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                >
                  <span>{tip.actionText}</span>
                  <ChevronRight size={13} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
